/**
 * Cliente WebSocket para /ws/live.
 *
 * Uma única conexão por aba (singleton em módulo). Reconecta com
 * backoff exponencial 1s → 2s → 4s → 8s → 16s → 30s. Responde `pong`
 * a cada `ping` do servidor para manter o heartbeat.
 *
 * API pública:
 *   useLiveEvent(type, handler)   — subscreve callback para um tipo
 *   useLiveConnection()           — { status, lastEventAt }
 *
 * Design: como o hook é usado em várias telas ao mesmo tempo, a
 * conexão precisa ser compartilhada. Um singleton em escopo de módulo
 * carrega essa responsabilidade — cada uso de `useLiveEvent`
 * acrescenta um listener na fila; cada `useLiveConnection` registra
 * um subscriber de estado.
 */
import { useEffect, useRef, useState } from 'react';

// ------ Tipos ------
export type LiveEvent =
  | { type: 'transaction.started'; payload: { txId: number; cpId: string; connectorId: number; idTag: string; startTime: string } }
  | { type: 'transaction.ended'; payload: { txId: number; cpId: string; energyWh: number; stopReason: string | null; stopTime: string } }
  | { type: 'transaction.metervalue'; payload: { txId: number; cpId: string; energyWh: number; powerW: number; soc?: number; ts: string } }
  | { type: 'charger.status'; payload: { cpId: string; connectorId: number; status: string; ts: string } }
  | { type: 'alert.created'; payload: { id: number; cpId: string | null; level: 'info' | 'warning' | 'error'; message: string; createdAt: string } }
  | { type: 'system.audit'; payload: { action: string; target: string | null; targetId: string | null; userEmail: string | null; createdAt: string } };

export type LiveEventType = LiveEvent['type'];
export type LiveEventPayload<T extends LiveEventType> = Extract<LiveEvent, { type: T }>['payload'];
export type LiveConnStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

// ------ Singleton de conexão ------

type AnyHandler = (payload: unknown, ts: string) => void;

const handlers = new Map<LiveEventType, Set<AnyHandler>>();
type ConnListener = (s: { status: LiveConnStatus; lastEventAt: number | null }) => void;
const statusListeners = new Set<ConnListener>();

let ws: WebSocket | null = null;
let status: LiveConnStatus = 'closed';
let lastEventAt: number | null = null;
let backoffMs = 1000;
let closedByUser = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let refs = 0;

function setStatus(s: LiveConnStatus) {
  status = s;
  const snap = { status, lastEventAt };
  for (const l of statusListeners) l(snap);
}

function emit<T extends LiveEventType>(type: T, payload: LiveEventPayload<T>, ts: string) {
  lastEventAt = Date.now();
  const set = handlers.get(type);
  if (!set) return;
  for (const h of set) {
    try { h(payload as unknown, ts); } catch { /* ignore */ }
  }
}

function scheduleReconnect() {
  if (closedByUser) return;
  if (reconnectTimer) return;
  setStatus('reconnecting');
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
    backoffMs = Math.min(30_000, backoffMs * 2);
  }, backoffMs);
}

function connect() {
  if (ws) return;
  closedByUser = false;
  setStatus(status === 'closed' ? 'connecting' : status);
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}/ws/live`;
  let socket: WebSocket;
  try {
    socket = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  ws = socket;
  setStatus('connecting');

  socket.addEventListener('open', () => {
    backoffMs = 1000;
    setStatus('open');
  });
  socket.addEventListener('close', () => {
    ws = null;
    if (closedByUser) { setStatus('closed'); return; }
    scheduleReconnect();
  });
  socket.addEventListener('error', () => {
    // "error" precede "close" — deixamos o close cuidar do reconnect
  });
  socket.addEventListener('message', (ev) => {
    try {
      const parsed = JSON.parse(String(ev.data)) as { type?: string; payload?: unknown; ts?: string };
      if (!parsed?.type) return;
      if (parsed.type === 'ping') {
        try { socket.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
        return;
      }
      if (parsed.type === 'hello') { lastEventAt = Date.now(); return; }
      const ts = parsed.ts ?? new Date().toISOString();
      emit(parsed.type as LiveEventType, (parsed.payload ?? {}) as LiveEventPayload<LiveEventType>, ts);
    } catch { /* ignore */ }
  });
}

function ensureConnected() {
  refs += 1;
  if (!ws && !reconnectTimer) connect();
}

function releaseConnection() {
  refs = Math.max(0, refs - 1);
  if (refs === 0) {
    closedByUser = true;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) { try { ws.close(); } catch { /* ignore */ } }
    ws = null;
    setStatus('closed');
  }
}

// ------ Hooks públicos ------

export function useLiveEvent<T extends LiveEventType>(
  type: T,
  handler: (payload: LiveEventPayload<T>, ts: string) => void,
): void {
  // Guarda referência estável ao handler mais recente para não re-subscrever
  // toda vez que o caller passar arrow inline.
  const ref = useRef(handler);
  useEffect(() => { ref.current = handler; }, [handler]);
  useEffect(() => {
    const wrapper: AnyHandler = (p, ts) => ref.current(p as LiveEventPayload<T>, ts);
    let set = handlers.get(type);
    if (!set) { set = new Set(); handlers.set(type, set); }
    set.add(wrapper);
    ensureConnected();
    return () => {
      set!.delete(wrapper);
      releaseConnection();
    };
  }, [type]);
}

export function useLiveConnection(): { status: LiveConnStatus; lastEventAt: number | null } {
  const [state, setState] = useState<{ status: LiveConnStatus; lastEventAt: number | null }>({ status, lastEventAt });
  useEffect(() => {
    const l: ConnListener = (s) => setState(s);
    statusListeners.add(l);
    ensureConnected();
    // Emite snapshot corrente pra alinhar com o hook novo
    l({ status, lastEventAt });
    return () => {
      statusListeners.delete(l);
      releaseConnection();
    };
  }, []);
  return state;
}
